import { Component, inject, input, output, signal, computed, effect, DestroyRef, ChangeDetectionStrategy, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';
import { RequestService, Request, MinistryDecision, CommissionDecision, PaymentDecision, CommissionVotoMatch } from '../../services/requests.service';
import { BpmService } from '../../services/bpm.service';
import { PartyFormComponent } from '../../../management-parties/components/party-form/party-form.component';
import { ThirdPartyFormComponent } from '../../../management-claim/components/third-party-form/third-party-form.component';
import { ClaimDetailsManagerComponent } from '../../../management-claim/components/claim-details-manager/claim-details-manager.component';
import { ClaimInformePdfComponent } from '../../../management-claim/components/claim-informe-pdf/claim-informe-pdf.component';
import { ReimbursementService } from '../../../management/reimbursement-management/services/reimbursement.service';
import { FormMode } from 'app/core/constants/form-mode';
import { labelForClaimType } from '../../../management-claim/utils/claim-details.util';
import { TimelineComponent, TimelineStep } from 'app/core/components/timeline/timeline.component';
import { FlowBadgeComponent } from 'app/core/components/flow-badge/flow-badge.component';
import { FlowStepsComponent } from 'app/core/components/flow-steps/flow-steps.component';
// Servicios de configuracion y entorno
import { AppSettings } from '@services/settings/app-settings.service';
import { AuthService } from '@services/core/auth.service';
import { CatalogService } from '@services/data/catalog.service';
import { ImageOptimizerService } from 'app/core/service/utils/image-optimizer.service';
import { Global } from '@services/core/Global';
import { ViewerComponent, DocViewerItem } from 'app/feature/management-document/components/viewer/viewer.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

import { StepApprovalPayload } from '../step-approval-modal/step-approval-modal.component';
import { BpmComponent } from '../bpm/bpm.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';

type VotoPreviewKind = 'image' | 'pdf' | 'unsupported' | 'none';

@Component({
  selector: 'app-request-claim',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule,
    PartyFormComponent,
    ThirdPartyFormComponent, ClaimDetailsManagerComponent, ClaimInformePdfComponent,
    TimelineComponent, FlowBadgeComponent, FlowStepsComponent, ViewerComponent,
    BpmComponent, MobileFooterComponent
  ],
  templateUrl: './request-claim.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./request-claim.component.scss']
})
export class RequestClaimComponent {
  request = input.required<Request>();
  reviewCompleted = output<boolean>();
  editorTitleChange = output<string | null>();

  private requestService = inject(RequestService);
  private reimbursementService = inject(ReimbursementService);
  private bpmService = inject(BpmService);
  private catalogService = inject(CatalogService);
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private imageOptimizer = inject(ImageOptimizerService);
  private sanitizer = inject(DomSanitizer);
  private modalService = inject(NgbModal);
  public appSettings = inject(AppSettings);

  @ViewChild(BpmComponent) bpmActions!: BpmComponent;
  @ViewChild(ClaimDetailsManagerComponent) detailsManager?: ClaimDetailsManagerComponent;
  @ViewChild('claimInforme') claimInforme?: ClaimInformePdfComponent;
  private activeModal: NgbModalRef | null = null;
  private destroyRef = inject(DestroyRef);
  private informeGeneratedForUuid: string | null = null;

  public partyForm!: FormGroup;
  public formMode = FormMode;

  selectedRequest = signal<Request | null>(null);
  currentStep = signal<number>(1);
  isEditingDetail = signal<boolean>(false);
  claimDetails = signal<any[]>([]);

  claimVotoFile = signal<File | null>(null);
  claimVotoFileName = signal<string>('');
  votoLocalPreviewUrl = signal<string | null>(null);
  votoGlosa = signal('');
  reusedVoto = signal<CommissionVotoMatch['document'] | null>(null);
  isLookingUpVoto = signal(false);
  isProcessingApproval = signal(false);
  isViewerOpen = signal(false);
  viewerItems = signal<DocViewerItem[]>([]);
  activeDocId = signal<string | number>('');
  tokenCopied = signal(false);
  private tokenCopiedTimer: ReturnType<typeof setTimeout> | null = null;
  private votoLookupTimer: ReturnType<typeof setTimeout> | null = null;

  private role = computed(() => {
    const roleObj = this.authService.sharingRole() as any;
    return (roleObj?.slug ?? roleObj?.name ?? 'VISUALIZADOR').toUpperCase();
  });

  private userMail = computed(() => {
    const identity = this.authService.sharingIdentity() as any;
    const data = this.authService.userData() as any;
    return String(identity?.mail ?? data?.mail ?? '').trim().toLowerCase() || null;
  });

  timelineSteps = computed<TimelineStep[]>(() => {
    const request = this.selectedRequest();
    if (!request) return [];

    if (request.processSlug === 'ACCIDENT_THIRD_PARTY') {
      return [
        { id: 1, title: 'Socio' },
        { id: 2, title: 'Afectado' },
        { id: 3, title: 'Siniestro' },
        { id: 4, title: 'Aprobación' }
      ];
    }
    if (request.processSlug === 'ACCIDENT' || request.processSlug === 'TOTAL_LOSS') {
      return [
        { id: 1, title: 'Socio' },
        { id: 2, title: 'Siniestro' },
        { id: 3, title: 'Aprobación' }
      ];
    }
    return [];
  });

  currentStepObj = computed(() => {
    const steps = this.timelineSteps();
    const currentId = this.currentStep();
    return steps.find(s => s.id === currentId) ?? null;
  });

  // ACCIDENT y TOTAL_LOSS comparten el mismo timeline, por lo que el tipo debe ser explícito
  claimTypeLabel = computed<string>(() => {
    const request = this.selectedRequest();
    const type = request?.claim?.claimType ?? request?.processSlug;
    return labelForClaimType(type);
  });

  canApprove = computed(() => this.bpmService.canApprove(this.selectedRequest(), this.role()));
  canReviewContent = computed(() => this.bpmService.canReviewContent(this.selectedRequest(), this.role()));
  canFinalContentReview = computed(() =>
    this.bpmService.canFinalContentReview(this.selectedRequest(), this.role())
  );
  canReplaceObserved = computed(() =>
    this.bpmService.canReplaceObservedDocuments(this.selectedRequest(), this.role(), this.userMail())
  );
  hasObservedEvidence = computed(() =>
    this.bpmService.hasObservedClaimDocuments(this.selectedRequest())
  );
  nextStatusSlug = computed(() => this.bpmService.nextStatusSlug(this.selectedRequest()));

  showApprovalMobileFooter = computed(() =>
    this.appSettings.isMobile()
    && this.currentStepObj()?.title === 'Aprobación'
    && !this.isEditingDetail()
    && this.canApprove()
  );

  canConfirmApproval = computed(() =>
    this.canApprove() && !this.isProcessingApproval() && !this.bpmBlockedReason()
  );

  approveMobileLabel = computed(() => {
    const next = this.nextStatusSlug();
    if (next === 'REIMBURSED') return 'Reembolso';
    if (this.isPaymentStep()) return 'Pago';
    if (this.stepRequiresDocument()) return 'Aprobar';
    return 'Aprobar';
  });

  onMobileApprove(): void {
    this.bpmActions?.onApproveClick();
  }

  onMobileReject(): void {
    this.bpmActions?.onRejectClick();
  }

  /**
   * El paso BPM actual exige documento (`requiresDocument` + `documentTypeSlug`
   * en status-flow). Independiente del rol revisor.
   */
  stepRequiresDocument = computed(() =>
    this.bpmService.stepRequiresDocument(this.selectedRequest())
  );

  /** @deprecated Preferir `stepRequiresDocument`. */
  requiresCommissionVote = computed(() => this.stepRequiresDocument());

  /** Puede cargar el documento quien aprueba o quien es revisor del paso. */
  canUploadVoto = computed(() =>
    this.stepRequiresDocument() && (this.canApprove() || this.canReviewContent())
  );

  /** Oculta el upload si el documento ya está en BD o se reutilizará por glosa. */
  needsVotoUpload = computed(() =>
    this.canUploadVoto() && !this.hasVotoInDb() && !this.reusedVoto()
  );

  /** Etiqueta del rol revisor / aprobador (prioriza contentReviewRole del BPM). */
  approverRoleLabel = computed(() => {
    const req = this.selectedRequest();
    const fromDecision = this.roleLabel(req?.commissionDecision?.actorRole);
    if (fromDecision) return fromDecision;
    const reviewer = this.roleLabel(this.bpmService.contentReviewRole(req));
    if (reviewer) return reviewer;
    return this.roleLabel(this.bpmService.requiredRole(req)) || 'Rol revisor';
  });

  /** Paso de registro de pago segun BPM. */
  isPaymentStep = computed(() => this.bpmService.isPaymentStep(this.selectedRequest()));

  canRegisterPayment = computed(() => this.canApprove() && this.isPaymentStep());

  currentStepDocumentTypeSlug = computed(() => {
    return this.bpmService.stepDocumentTypeSlug(this.selectedRequest()) ?? '';
  });

  currentStepDocumentTypeName = computed(() => {
    return this.bpmService.stepDocumentTypeName(this.selectedRequest(), 'Documento');
  });

  bpmBlockedReason = computed(() => {
    if (!this.hasObservedEvidence()) return null;
    return 'Hay archivos observados. Espere el reemplazo de la entidad antes de aprobar.';
  });

  votoDocument = computed(() => {
    const docs = this.selectedRequest()?.claim?.documents ?? [];
    const targetSlug = this.currentStepDocumentTypeSlug();
    if (!targetSlug) return null;
    const matches = docs.filter((d: any) =>
      d.typeSlug === targetSlug || d.type?.slug === targetSlug
    );
    return matches.length ? matches[matches.length - 1] : null;
  });

  /** Solo documentos del tipo requerido ya persistidos (no el archivo local). */
  hasVotoInDb = computed(() => !!this.votoDocument());

  /** UUID del archivo (Document), para GET /V1/documents/view/:uuid */
  votoFileMeta = computed(() => {
    const docName = this.currentStepDocumentTypeName();
    const reused = this.reusedVoto();
    if (reused?.uuid && !this.claimVotoFile()) {
      return {
        uuid: reused.uuid,
        fileName: reused.fileName || `${docName} adjunto`,
        mimeType: reused.mimeType || null,
        typeName: docName,
        uploadedAt: null as string | null,
        viewPath: `V1/documents/view/${reused.uuid}`,
        reused: true
      };
    }
    const d = this.votoDocument();
    if (!d) return null;
    const file = d.document ?? {};
    const uuid = file.uuid || file.fileUuid || null;
    return {
      uuid,
      fileName: file.fileName || d.fileName || d.originalName || d.type?.name || `${docName} adjunto`,
      mimeType: file.mimeType || d.mimeType || null,
      typeName: d.type?.name || docName,
      uploadedAt: file.createdAt || d.createdAt || d.approvalDate || null,
      viewPath: uuid ? `V1/documents/view/${uuid}` : null,
      reused: false
    };
  });

  votoTexto = computed(() => this.bpmService.documentGloss(this.selectedRequest()));

  /** Columna derecha: voto en BD, archivo local, o placeholder discreto en modo comisión. */
  showVotoPreviewPanel = computed(() => this.hasVotoInDb() || this.canUploadVoto() || !!this.claimVotoFile());

  votoPreviewKind = computed<VotoPreviewKind>(() => {
    const local = this.claimVotoFile();
    if (local) return this.kindFromNameAndMime(local.name, local.type, 'unsupported');
    const meta = this.votoFileMeta();
    if (!meta?.viewPath) return 'none';
    return this.kindFromNameAndMime(meta.fileName, meta.mimeType, 'pdf');
  });

  votoPreviewRawUrl = computed(() => {
    if (this.claimVotoFile()) return this.votoLocalPreviewUrl();
    const path = this.votoFileMeta()?.viewPath;
    return path ? Global.url + path : null;
  });

  votoPreviewImgUrl = computed(() => {
    if (this.votoPreviewKind() !== 'image') return null;
    const url = this.votoPreviewRawUrl();
    return url ? this.sanitizer.bypassSecurityTrustUrl(url) : null;
  });

  votoPreviewPdfUrl = computed<SafeResourceUrl | null>(() => {
    if (this.votoPreviewKind() !== 'pdf') return null;
    const url = this.votoPreviewRawUrl();
    return url ? this.sanitizer.bypassSecurityTrustResourceUrl(url) : null;
  });

  canOpenVotoPreview = computed(() => !!this.votoPreviewRawUrl());

  /** Ficha del ministro: fecha y actor de PENDING → MINISTRY_APPROVED. */
  ministrySummary = computed(() => {
    const req = this.selectedRequest();
    if (!req) return null;

    const claim = req.claim;
    const md: MinistryDecision | undefined = req.ministryDecision;
    const statusSlug = req.statusSlug || '';
    const isPending = statusSlug === 'PENDING';
    const ministryRejected = !!(md?.status === 'REJECTED' || (statusSlug === 'REJECTED' && md?.decidedAt && !claim?.ministroApprovalDate));
    const hasMinistrySeal = !!(claim?.ministroApprovedBy || claim?.ministroApprovalDate);
    const hasFlowDecision = !!(md?.decidedAt && (md.toStatusSlug === 'MINISTRY_APPROVED' || md.status === 'CONFIRMED' || md.status === 'REJECTED'));
    const showDecision = !isPending && (hasMinistrySeal || hasFlowDecision || ministryRejected);

    const decidedAt = showDecision
      ? (claim?.ministroApprovalDate || md?.decidedAt || null)
      : null;

    return {
      statusLabel: isPending
        ? 'Pendiente'
        : (ministryRejected ? 'Rechazado' : (showDecision ? 'Visto bueno otorgado' : this.statusDisplayLabel(statusSlug))),
      isPending,
      isRejected: ministryRejected,
      showDecision,
      decidedAt,
      approverName: showDecision ? this.cleanActorLabel(claim?.ministroApprovedBy || md?.actorLabel) : null,
      approverMail: showDecision ? (md?.actorMail || this.mailFromLabel(claim?.ministroApprovedBy || md?.actorLabel)) : null,
      approverRole: showDecision ? (this.roleLabel(md?.actorRole) || 'Ministro de Fe') : null,
      channel: this.channelLabel(md?.channel || (isPending ? (req.confirmationToken ? 'TOKEN' : 'INBOX') : null)),
      token: isPending ? (req.confirmationToken || null) : null,
      tokenDisplay: this.formatTokenDisplay(isPending ? req.confirmationToken : null),
      tokenExpiresAt: isPending ? (req.tokenExpiresAt || md?.tokenExpiresAt || null) : null,
      rejectionReason: ministryRejected ? (req.rejectionReason || null) : null
    };
  });

  /** Ficha de la comisión / rol revisor: solo aparece al llegar a ese paso (no antes). */
  commissionSummary = computed(() => {
    const req = this.selectedRequest();
    if (!req) return null;

    const cd: CommissionDecision | null | undefined = req.commissionDecision;
    const statusSlug = req.statusSlug || '';
    // Aún en ministro: no adelantar la card del siguiente paso.
    if (statusSlug === 'PENDING') return null;

    const ministryRejected = !!(req.ministryDecision?.status === 'REJECTED'
      || (statusSlug === 'REJECTED' && req.ministryDecision?.decidedAt && !req.claim?.ministroApprovalDate));
    const isRejected = statusSlug === 'REJECTED' && (!!cd?.decidedAt || !!req.rejectionReason) && !ministryRejected;

    // Paso actual exige documento (status-flow). Si el request sigue aquí, la card es PENDIENTE.
    const onCommissionStep = this.bpmService.stepRequiresDocument(req);
    const leftCommissionStep = !onCommissionStep
      && statusSlug !== 'PENDING'
      && (['APPROVED', 'PENDING_PAYMENT', 'PENDING_REIMBURSEMENT', 'PAID', 'REIMBURSED'].includes(statusSlug)
        || !!(cd?.decidedAt && cd.status !== 'REJECTED')
        || !!this.bpmService.documentGloss(req));

    const isPending = onCommissionStep && !isRejected;
    const showCard = onCommissionStep || leftCommissionStep || isRejected;
    if (!showCard) return null;

    const showDecision = !isPending && (leftCommissionStep || isRejected);
    const votoFile = this.votoFileMeta();
    const votoKind = votoFile ? this.fileKindLabel(votoFile.fileName, votoFile.mimeType) : null;
    // En el paso en curso no mostramos glosa/voto viejos como si ya hubiera aprobado.
    const glosa = showDecision ? (this.votoGlosa().trim() || this.votoTexto() || null) : null;

    let statusLabel = 'Pendiente';
    if (isRejected) statusLabel = 'Rechazada';
    else if (showDecision) statusLabel = 'Aprobada';

    return {
      statusLabel,
      isPending,
      isRejected,
      showCard: true,
      showDecision,
      decidedAt: showDecision ? (cd?.decidedAt || null) : null,
      approverName: showDecision ? this.cleanActorLabel(cd?.actorLabel) : null,
      approverMail: showDecision ? (cd?.actorMail || this.mailFromLabel(cd?.actorLabel)) : null,
      approverRole: showDecision
        ? (this.roleLabel(cd?.actorRole) || this.approverRoleLabel())
        : this.approverRoleLabel(),
      channel: this.channelLabel(showDecision ? cd?.channel : 'INBOX'),
      votoNumber: glosa,
      actaName: showDecision ? (votoFile?.fileName || null) : null,
      actaType: showDecision && votoFile ? [votoFile.typeName, votoKind].filter(Boolean).join(' · ') : null,
      actaUploadedAt: showDecision ? (votoFile?.uploadedAt || null) : null,
      hasActa: showDecision && !!votoFile,
      reusedActa: showDecision && !!votoFile?.reused,
      rejectionReason: isRejected ? (req.rejectionReason || null) : null
    };
  });

  /** Ficha de pago: aparece al llegar al paso de pago; datos desde reembolso / paymentDecision. */
  paymentSummary = computed(() => {
    const req = this.selectedRequest();
    if (!req) return null;

    const statusSlug = req.statusSlug || '';
    if (['PENDING', 'MINISTRY_APPROVED', 'REJECTED'].includes(statusSlug)) return null;

    const pd: PaymentDecision | null | undefined = req.paymentDecision;
    const reimb = (req as any).reimbursements?.[0] || null;
    const receiptDoc = reimb?.documents?.[0]?.document || null;
    const isPaid = statusSlug === 'PAID' || statusSlug === 'REIMBURSED';
    const label = this.statusDisplayLabel(statusSlug);
    const looksLikePaymentStep = /pago|reembolso/i.test(label) || ['APPROVED', 'PENDING_PAYMENT', 'PENDING_REIMBURSEMENT'].includes(statusSlug);
    const pastCommission = !!(req.commissionDecision?.decidedAt) || !!this.bpmService.documentGloss(req);
    const isPendingPayment = !isPaid && (looksLikePaymentStep || pastCommission);
    if (!isPaid && !isPendingPayment) return null;

    const statusLabel = isPaid ? (statusSlug === 'REIMBURSED' ? 'Reembolsado' : 'Pagado') : 'Pendiente';

    return {
      statusLabel,
      isPaid,
      isPending: isPendingPayment,
      decidedAt: isPaid ? (pd?.decidedAt || reimb?.paidAt || null) : null,
      approverName: isPaid ? this.cleanActorLabel(pd?.actorLabel) : null,
      approverMail: isPaid ? (pd?.actorMail || this.mailFromLabel(pd?.actorLabel)) : null,
      approverRole: isPaid
        ? (this.roleLabel(pd?.actorRole) || this.roleLabel(this.bpmService.requiredRole(req)) || 'Secretaría UCH')
        : (this.roleLabel(this.bpmService.requiredRole(req)) || 'Secretaría UCH'),
      channel: this.channelLabel(isPaid ? (pd?.channel || 'INBOX') : 'INBOX'),
      amount: reimb?.amount != null ? Number(reimb.amount) : null,
      paymentDate: isPaid ? (reimb?.paidAt || null) : null,
      transactionNumber: isPaid ? (reimb?.transactionNumber || null) : null,
      transferLabel: isPaid ? this.transferDestinationLabel(reimb?.transferTarget, req) : null,
      hasReceipt: isPaid && !!receiptDoc?.uuid,
      receiptUuid: receiptDoc?.uuid || null
    };
  });

  payTxNumber = signal('');
  payDate = signal(new Date().toISOString().split('T')[0]);
  payTransferTarget = signal<'CAMPUS' | 'SOCIO'>('CAMPUS');
  payFile = signal<File | null>(null);
  payFileName = signal('');
  payPreviewUrl = signal<string | null>(null);
  payPreviewKind = signal<'image' | 'pdf' | 'none'>('none');
  payPreviewSafeUrl = computed<SafeResourceUrl | null>(() => {
    const url = this.payPreviewUrl();
    if (!url || this.payPreviewKind() !== 'pdf') return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });
  payPreviewImgUrl = computed(() => {
    const url = this.payPreviewUrl();
    if (!url || this.payPreviewKind() !== 'image') return null;
    return this.sanitizer.bypassSecurityTrustUrl(url);
  });

  openVotoModal(): void {
    this.bpmActions?.openModal();
  }

  closeVotoModal(): void {
    this.bpmActions?.closeModal();
  }

  openPayModal(): void {
    this.bpmActions?.openModal();
  }

  closePayModal(): void {
    this.bpmActions?.closeModal();
  }

  campusPayeeName = computed(() => this.campusNameFromRequest(this.selectedRequest()));

  socioPayeeName = computed(() => this.selectedRequest()?.party?.names || 'el socio');

  payGlossPreview = computed(() => {
    const name = this.payTransferTarget() === 'SOCIO' ? this.socioPayeeName() : this.campusPayeeName();
    return `Tr. ${this.formatDateDot(this.payDate())} ${name}`;
  });

  private campusNameFromRequest(req: Request | null | undefined): string {
    const entity = req?.entity
      || this.catalogService.entities().find(e => Number(e.code) === Number(req?.entityCode));
    return String(entity?.campus || entity?.name || `Entidad ${req?.entityCode ?? ''}`).trim();
  }

  private transferDestinationLabel(target: string | undefined, req: Request): string {
    if (String(target || '').toUpperCase() === 'SOCIO') {
      return `Al socio (${req.party?.names || 'socio'})`;
    }
    return `Al campus (${this.campusNameFromRequest(req)})`;
  }

  private formatDateDot(value: string | null | undefined): string {
    if (!value) return 'N/A';
    const raw = String(value).slice(0, 10);
    const [y, m, d] = raw.split('-');
    if (y && m && d) return `${d}.${m}.${y}`;
    return raw;
  }

  onPayTxInput(event: Event): void {
    this.payTxNumber.set((event.target as HTMLInputElement).value ?? '');
  }

  onPayDateInput(event: Event): void {
    this.payDate.set((event.target as HTMLInputElement).value ?? '');
  }

  onPayFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.clearPayFile();
    if (!file) return;
    this.payFile.set(file);
    this.payFileName.set(file.name);
    const url = URL.createObjectURL(file);
    this.payPreviewUrl.set(url);
    if (file.type.startsWith('image/')) this.payPreviewKind.set('image');
    else if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) this.payPreviewKind.set('pdf');
    else this.payPreviewKind.set('none');
  }

  private clearPayFile(): void {
    const url = this.payPreviewUrl();
    if (url) URL.revokeObjectURL(url);
    this.payFile.set(null);
    this.payFileName.set('');
    this.payPreviewUrl.set(null);
    this.payPreviewKind.set('none');
  }

  payBlockedReason = computed(() => {
    if (this.isProcessingApproval()) return 'Procesando...';
    if (!this.payTxNumber().trim()) return 'Indique el número de transacción.';
    if (!this.payDate()) return 'Indique la fecha de pago.';
    if (!this.payFile()) return 'Adjunte el comprobante de pago.';
    return '';
  });

  constructor() {
    this.partyForm = this.fb.group({
      uuid: [null],
      rut: [null],
      partyTypeCode: [null],
      names: [null],
      birthday: [null],
      mail: [null],
      mobileNumber: [null],
      groupCode: [null],
      entityCode: [null],
    });

    this.destroyRef.onDestroy(() => {
      this.clearVotoLocalPreview();
      this.clearPayFile();
      if (this.tokenCopiedTimer) clearTimeout(this.tokenCopiedTimer);
      if (this.votoLookupTimer) clearTimeout(this.votoLookupTimer);
    });

    effect(() => {
      const title = this.isEditingDetail() ? 'Evidencias del daño' : null;
      queueMicrotask(() => this.editorTitleChange.emit(title));
    });

    effect(() => {
      const req = this.request();
      const prevUuid = this.selectedRequest()?.uuid;
      this.selectedRequest.set(req);
      if (req.party) {
        this.partyForm.patchValue(req.party);
      }
      if (req.uuid !== prevUuid) {
        this.resetVotoDraft(this.bpmService.documentGloss(req));
        this.isViewerOpen.set(false);
        this.modalService.dismissAll();
        this.activeModal = null;
        this.clearPayFile();
        this.isEditingDetail.set(false);
        this.currentStep.set(this.landingStepFor(req));
        this.informeGeneratedForUuid = null;
      } else if (!this.votoGlosa() && this.bpmService.documentGloss(req)) {
        this.votoGlosa.set(this.bpmService.documentGloss(req));
      }
      this.claimDetails.set(req.claim?.details ?? []);
    }, { allowSignalWrites: true });

    effect(() => {
      const onApproval = this.currentStepObj()?.title === 'Aprobación';
      const uuid = this.selectedRequest()?.uuid ?? null;
      if (!onApproval) {
        this.informeGeneratedForUuid = null;
        return;
      }
      if (!uuid || uuid === this.informeGeneratedForUuid) return;
      queueMicrotask(() => this._generateClaimInforme());
    });
  }

  private _informeContext(): { claim: any; party: any } | null {
    const req = this.selectedRequest();
    const raw = req?.claim;
    if (!raw) return null;
    const details = this.claimDetails().length ? this.claimDetails() : (Array.isArray(raw.details) ? raw.details : []);
    const vehicle = (raw as any).vehicle ?? (req as any)?.vehicle ?? null;
    return {
      claim: { ...raw, request: req, vehicle, details },
      party: req?.party ?? (raw as any).party ?? null
    };
  }

  private async _generateClaimInforme(attempt = 0): Promise<void> {
    const ctx = this._informeContext();
    if (!ctx) return;
    if (!this.claimInforme) {
      if (attempt < 20) setTimeout(() => this._generateClaimInforme(attempt + 1), 100);
      return;
    }
    const uuid = this.selectedRequest()?.uuid ?? null;
    this.informeGeneratedForUuid = uuid;
    await this.claimInforme.generate(ctx);
  }

  openClaimInforme(): void {
    if (!this.claimInforme) return;
    const ctx = this._informeContext();
    if (!ctx) {
      Swal.fire('Sin informe', 'Esta solicitud no tiene un siniestro asociado para generar el informe.', 'info');
      return;
    }
    if (this.claimInforme.pdfSrcUrl()) {
      this.claimInforme.openGeneratedModal();
      return;
    }
    void this.claimInforme.openPreview(ctx);
  }

  closeDetailEditor(): void {
    this.detailsManager?.closeDetailEditor();
    this.isEditingDetail.set(false);
  }

  loadRequests() {
    this.requestService.loadRequests();
  }

  onReviewCompleted() {
    this.reviewCompleted.emit(false);
  }

  private finishAdvance(keepOpen: boolean): void {
    if (keepOpen) this.goToApproval();
    this.reviewCompleted.emit(keepOpen);
  }

  private goToApproval(): void {
    const steps = this.timelineSteps();
    const id = steps.find(s => s.title === 'Aprobación')?.id ?? steps[steps.length - 1]?.id ?? 1;
    this.currentStep.set(id);
  }

  onVotoFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.reusedVoto.set(null);
    this.setVotoLocalFile(file);
  }

  onVotoGlosaInput(event: Event) {
    const value = (event.target as HTMLInputElement).value ?? '';
    this.votoGlosa.set(value);
    this.scheduleVotoLookup(value);
  }

  effectiveVotoGlosa(): string {
    return (this.votoGlosa().trim() || this.votoTexto() || '').trim();
  }

  private landingStepFor(req: Request): number {
    if (['PAID', 'REIMBURSED', 'REJECTED'].includes(req.statusSlug || '') || this.isPaymentStatus(req)) {
      return this.resultStepIdFor(req);
    }
    if (this.bpmService.canReviewContent(req, this.role()) && req.statusSlug !== 'PENDING') {
      return this.siniestroStepIdFor(req);
    }
    if (this.bpmService.isVisibleToOriginInbox(req, this.role())) {
      return this.siniestroStepIdFor(req);
    }
    return 1;
  }

  private resultStepIdFor(req: Request): number {
    const thirdParty = req.processSlug === 'ACCIDENT_THIRD_PARTY';
    return thirdParty ? 4 : 3;
  }

  private siniestroStepIdFor(req: Request): number {
    return req.processSlug === 'ACCIDENT_THIRD_PARTY' ? 3 : 2;
  }

  private isPaymentStatus(req: Request): boolean {
    if (!req || ['PENDING', 'PAID', 'REIMBURSED', 'REJECTED'].includes(req.statusSlug || '')) {
      return false;
    }
    if (this.bpmService.stepRequiresDocument(req)) return false;
    const label = this.statusDisplayLabel(req.statusSlug);
    return /pago|reembolso/i.test(label) || ['APPROVED', 'PENDING_PAYMENT', 'PENDING_REIMBURSEMENT'].includes(req.statusSlug || '');
  }

  private resetVotoDraft(existingGlosa?: string | null) {
    this.setVotoLocalFile(null);
    this.reusedVoto.set(null);
    this.votoGlosa.set(String(existingGlosa ?? '').trim());
    this.isLookingUpVoto.set(false);
  }

  private scheduleVotoLookup(raw: string) {
    if (this.votoLookupTimer) clearTimeout(this.votoLookupTimer);
    const glosa = raw.trim();
    if (!glosa || !this.canUploadVoto()) {
      this.reusedVoto.set(null);
      this.isLookingUpVoto.set(false);
      return;
    }
    this.votoLookupTimer = setTimeout(() => this.lookupVotoByGlosa(glosa), 400);
  }

  private lookupVotoByGlosa(glosa: string) {
    const currentUuid = this.selectedRequest()?.uuid;
    this.isLookingUpVoto.set(true);
    this.requestService.findCommissionVoto(glosa, this.selectedRequest()?.entityCode).subscribe({
      next: (res) => {
        this.isLookingUpVoto.set(false);
        if (this.votoGlosa().trim().toLowerCase() !== glosa.toLowerCase()) return;
        if (!res?.found || !res.document?.uuid || res.requestUuid === currentUuid) {
          this.reusedVoto.set(null);
          return;
        }
        this.setVotoLocalFile(null);
        this.reusedVoto.set(res.document);
      },
      error: () => {
        this.isLookingUpVoto.set(false);
        this.reusedVoto.set(null);
      }
    });
  }

  openVotoDocument() {
    const url = this.votoPreviewRawUrl();
    if (!url) return;
    const kind = this.votoPreviewKind();
    const local = this.claimVotoFile();
    const id = local ? 'local-voto' : (this.votoFileMeta()?.uuid || 'voto');
    const fileName = local?.name || this.votoFileMeta()?.fileName || 'Acta / Voto de Comisión';
    const mimeType = local?.type || this.votoFileMeta()?.mimeType || (kind === 'pdf' ? 'application/pdf' : null);
    this.viewerItems.set([{
      id,
      title: fileName,
      imageUrl: url,
      status: 'success',
      mimeType,
      fileName,
      kind: kind === 'pdf' ? 'pdf' : 'image'
    }]);
    this.activeDocId.set(id);
    this.isViewerOpen.set(true);
  }

  private setVotoLocalFile(file: File | null) {
    this.clearVotoLocalPreview();
    this.claimVotoFile.set(file);
    this.claimVotoFileName.set(file?.name ?? '');
    if (file) {
      this.votoLocalPreviewUrl.set(URL.createObjectURL(file));
    }
  }

  private clearVotoLocalPreview() {
    const url = this.votoLocalPreviewUrl();
    if (url) URL.revokeObjectURL(url);
    this.votoLocalPreviewUrl.set(null);
  }

  private kindFromNameAndMime(
    fileName?: string | null,
    mimeType?: string | null,
    fallback: VotoPreviewKind = 'unsupported'
  ): VotoPreviewKind {
    const name = fileName ?? '';
    const mime = mimeType ?? '';
    if (mime.startsWith('image/') || /\.(jpe?g|png|gif|webp)$/i.test(name)) return 'image';
    if (mime === 'application/pdf' || mime.includes('pdf') || /\.pdf$/i.test(name)) return 'pdf';
    return fallback;
  }

  private fileKindLabel(fileName?: string | null, mimeType?: string | null): string | null {
    const kind = this.kindFromNameAndMime(fileName, mimeType, 'unsupported');
    if (kind === 'pdf') return 'PDF';
    if (kind === 'image') return 'Imagen';
    return null;
  }

  private formatTokenDisplay(token?: string | null): string | null {
    if (!token) return null;
    if (token.length <= 12) return token;
    return `…${token.slice(-8)}`;
  }

  private channelLabel(channel?: string | null): string | null {
    if (!channel) return null;
    const map: Record<string, string> = {
      TOKEN: 'Por token',
      INBOX: 'Bandeja',
      SYSTEM: 'Sistema'
    };
    return map[channel] || channel;
  }

  private roleLabel(role?: string | null): string | null {
    if (!role) return null;
    const map: Record<string, string> = {
      ministro_fe: 'Ministro de Fe',
      sec_uch: 'Secretaría UCH',
      sec: 'Secretaría',
      tesorero_uch: 'Tesorero',
      adm: 'Administrador',
      support: 'Soporte',
      encargado: 'Encargado',
      party: 'Socio'
    };
    return map[role.toLowerCase()] || role;
  }

  private statusDisplayLabel(slug?: string | null): string {
    const req = this.selectedRequest();
    if (req?.statusObj?.name && (!slug || slug === req.statusSlug)) {
      return req.statusObj.name;
    }
    if (slug) {
      const fromCatalog = this.catalogService.getStatusBySlug(slug)?.name;
      if (fromCatalog) return fromCatalog;
    }
    const map: Record<string, string> = {
      PENDING: 'Pendiente de visto bueno',
      MINISTRY_APPROVED: 'Visto bueno del ministro',
      APPROVED: 'Aprobada',
      PENDING_PAYMENT: 'Pendiente de pago',
      PAID: 'Pagado',
      PENDING_REIMBURSEMENT: 'Proceso de Reembolso',
      REIMBURSED: 'Reembolsado',
      REJECTED: 'Rechazada'
    };
    return (slug && map[slug]) || slug || 'Sin estado';
  }

  private cleanActorLabel(raw?: string | null): string | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    const mailOnly = trimmed.match(/^\(([^)]+@[^)]+)\)$/);
    if (mailOnly) return mailOnly[1];
    const withMail = trimmed.match(/^(.*?)\s*\(([^)]+@[^)]+)\)\s*$/);
    if (withMail) {
      const name = withMail[1].trim();
      return name || withMail[2];
    }
    return trimmed;
  }

  private mailFromLabel(raw?: string | null): string | null {
    if (!raw) return null;
    const match = raw.match(/\(([^)]+@[^)]+)\)/);
    return match ? match[1] : (raw.includes('@') ? raw : null);
  }

  async copyApprovalToken(): Promise<void> {
    const token = this.ministrySummary()?.token;
    if (!token) return;
    try {
      const confirmUrl = `${window.location.origin}/confirm-request?token=${token}`;
      await navigator.clipboard.writeText(confirmUrl);
      this.tokenCopied.set(true);
      if (this.tokenCopiedTimer) clearTimeout(this.tokenCopiedTimer);
      this.tokenCopiedTimer = setTimeout(() => this.tokenCopied.set(false), 2000);
    } catch {
      this.tokenCopied.set(false);
    }
  }

  async rejectRequest() {
    if (!this.selectedRequest() || this.isProcessingApproval()) return;
    const { value: reason } = await Swal.fire({
      title: 'Rechazar Solicitud',
      input: 'textarea',
      inputLabel: 'Motivo del Rechazo (Obligatorio)',
      inputPlaceholder: 'Escriba por qué se rechaza...',
      inputValidator: (v) => !v ? 'El motivo es obligatorio para rechazar' : null,
      showCancelButton: true,
      confirmButtonText: 'Confirmar Rechazo',
      confirmButtonColor: '#d33'
    });
    if (!reason) return;
    this.isProcessingApproval.set(true);
    this.requestService.advanceFlowStep(this.selectedRequest()!.uuid, {
      toStatusSlug: 'REJECTED',
      reason
    }).subscribe({
      next: () => {
        this.isProcessingApproval.set(false);
        this.onReviewCompleted();
      },
      error: () => this.isProcessingApproval.set(false)
    });
  }

  async approveRequest() {
    if (!this.selectedRequest() || this.isProcessingApproval()) return;
    if (this.hasObservedEvidence()) {
      await Swal.fire({
        icon: 'warning',
        title: 'Corrección pendiente',
        text: 'Hay archivos observados. La entidad debe reemplazarlos antes de aprobar la solicitud.'
      });
      return;
    }

    const gapSync = await this.syncToPendingGapIfNeeded();
    if (gapSync !== 'ok') return;

    if (this.stepRequiresDocument() || this.isPaymentStep()) {
      this.bpmActions.openModal();
      return;
    }

    const result = await Swal.fire({
      title: 'Aprobar',
      text: '¿Confirmar y avanzar al siguiente paso?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, aprobar'
    });
    if (!result.isConfirmed) return;

    await this.executeApproval();
  }

  /**
   * Si el snapshot tiene un paso pendiente detrás del status (p. ej. se agregó
   * Borrador), rebobina al gap antes de exigir VOTO del paso adelantado.
   */
  private async syncToPendingGapIfNeeded(): Promise<'ok' | 'aborted' | 'reloaded'> {
    const req = this.selectedRequest();
    if (!req || !this.bpmService.hasBehindGap(req)) return 'ok';

    const gapSlug = this.bpmService.pendingGapStatusSlug(req);
    if (!gapSlug) return 'ok';
    const label = this.bpmService.pendingGapLabel(req) || gapSlug;

    const confirm = await Swal.fire({
      icon: 'info',
      title: 'Paso pendiente anterior',
      html: `El flujo BPM tiene un paso sin completar (<b>${label}</b>).<br>
        Se rebobinará la solicitud a ese paso para poder continuar.`,
      showCancelButton: true,
      confirmButtonText: 'Rebobinar y continuar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirm.isConfirmed) return 'aborted';

    this.isProcessingApproval.set(true);
    try {
      await firstValueFrom(this.requestService.advanceFlowStep(req.uuid, {
        toStatusSlug: gapSlug
      }));
      this.reviewCompleted.emit(true);
      await Swal.fire({
        icon: 'success',
        title: 'Solicitud rebobinada',
        text: `Quedó en «${label}». Vuelva a aprobar ese paso.`
      });
      return 'reloaded';
    } catch (err: any) {
      const msg = err?.error?.error || err?.error?.message || err?.message;
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo rebobinar',
        text: msg || 'Intente aplicar el flujo a solicitudes abiertas desde Configuración BPM.'
      });
      return 'aborted';
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  async onStepApprovalConfirmed(payload: StepApprovalPayload): Promise<void> {
    if (!this.selectedRequest()) return;
    if (this.isPaymentStep() && payload.payDetails) {
      await this.executePaymentApproval(payload);
      return;
    }
    await this.executeDocumentApproval(payload);
  }

  private async executePaymentApproval(payload: StepApprovalPayload): Promise<void> {
    this.isProcessingApproval.set(true);
    try {
      const file = payload.file;
      const toUpload = file?.type.startsWith('image/')
        ? ((await this.imageOptimizer.optimize(file)) || file)
        : (file || undefined);
      await firstValueFrom(this.reimbursementService.payByRequest(
        this.selectedRequest()!.uuid,
        payload.payDetails!.txNumber,
        payload.payDetails!.payDate,
        toUpload,
        payload.payDetails!.target
      ));
      this.bpmActions.closeModal();
      this.onReviewCompleted();
      await Swal.fire({
        icon: 'success',
        title: 'Pago registrado',
        text: 'El comprobante quedó guardado y la solicitud quedó Aprobada.'
      });
    } catch (err: any) {
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo registrar el pago',
        text: err?.error?.error || 'Intente nuevamente.'
      });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  private async executeDocumentApproval(payload: StepApprovalPayload): Promise<void> {
    const claimUuid = this.selectedRequest()?.claim?.uuid;
    const docSlug = payload.documentTypeSlug ?? this.currentStepDocumentTypeSlug();
    let docUuid = payload.documentUuid;
    this.isProcessingApproval.set(true);
    try {
      if (payload.file && claimUuid) {
        const file = payload.file.type.startsWith('image/')
          ? ((await this.imageOptimizer.optimize(payload.file)) || payload.file)
          : payload.file;
        const res = await firstValueFrom(this.requestService.uploadClaimDocument(claimUuid, file, docSlug));
        docUuid = res?.document?.uuid ?? res?.uuid ?? docUuid;
      }
      const nextSlug = this.nextStatusSlug();
      await firstValueFrom(this.requestService.advanceFlowStep(this.selectedRequest()!.uuid, {
        toStatusSlug: nextSlug,
        gloss: payload.gloss,
        documentUuid: docUuid,
        documentTypeName: payload.documentTypeName,
        requiresDocument: payload.requiresDocument,
        approvedDeductible: payload.approvedDeductible,
        approvedAmount: payload.approvedAmount,
        invoicedAmount: payload.invoicedAmount
      }));
      this.bpmActions.closeModal();
      const keepOpen = this.bpmService.shouldStayOpenAfterAdvance(
        this.selectedRequest(),
        nextSlug,
        this.role()
      );
      this.finishAdvance(keepOpen);
    } catch (err: any) {
      const msg = err?.error?.error || err?.error?.message || err?.message;
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo completar la aprobación',
        text: msg || 'Intente nuevamente.'
      });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  private async executeApproval() {
    if (!this.selectedRequest()) return;
    const needsDocument = this.stepRequiresDocument();
    const glosa = this.effectiveVotoGlosa();

    this.isProcessingApproval.set(true);
    try {
      if (needsDocument) {
        const linked = await this.ensureCommissionVotoLinked();
        if (!linked) return;
      }

      const nextSlug = this.nextStatusSlug();
      await firstValueFrom(this.requestService.advanceFlowStep(this.selectedRequest()!.uuid, {
        toStatusSlug: nextSlug,
        ...(needsDocument ? { gloss: glosa } : {})
      }));
      const keepOpen = this.bpmService.shouldStayOpenAfterAdvance(
        this.selectedRequest(),
        nextSlug,
        this.role()
      );
      this.finishAdvance(keepOpen);
    } catch (err: any) {
      const apiMsg = err?.error?.error || err?.error?.message || err?.message;
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo completar la aprobación',
        text: apiMsg
          || 'Intente nuevamente. Si el voto ya se adjuntó, no es necesario volver a seleccionarlo.'
      });
    } finally {
      this.isProcessingApproval.set(false);
    }
  }

  private async ensureCommissionVotoLinked(): Promise<boolean> {
    if (this.claimVotoFile()) return this.uploadPendingVoto();
    if (this.reusedVoto()?.uuid) return this.attachReusedVoto();
    return this.hasVotoInDb();
  }

  private _setClaimDocuments(req: Request, doc: any, typeSlug: string): void {
    const docs = req.claim?.documents || [];
    const filtered = docs.filter((d: any) => (d.typeSlug || d.type?.slug) !== typeSlug);
    req.claim!.documents = [...filtered, doc];
    this.selectedRequest.set({ ...req });
  }

  private async attachReusedVoto(): Promise<boolean> {
    const documentUuid = this.reusedVoto()?.uuid;
    const claimUuid = this.selectedRequest()?.claim?.uuid;
    if (!documentUuid || !claimUuid) return false;
    const docSlug = this.currentStepDocumentTypeSlug();
    try {
      const res = await firstValueFrom(
        this.requestService.reuseClaimDocument(claimUuid, documentUuid, docSlug)
      );
      const req = this.selectedRequest();
      if (req?.claim) {
        this._setClaimDocuments(req, res, docSlug);
      }
      return true;
    } catch {
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo reutilizar el documento',
        text: 'Intente adjuntar el archivo o busque de nuevo la glosa.'
      });
      return false;
    }
  }

  private async uploadPendingVoto(): Promise<boolean> {
    const file = this.claimVotoFile();
    const claimUuid = this.selectedRequest()?.claim?.uuid;
    if (!file || !claimUuid) return false;
    const docSlug = this.currentStepDocumentTypeSlug();

    try {
      const toUpload = file.type.startsWith('image/')
        ? ((await this.imageOptimizer.optimize(file)) || file)
        : file;
      const res = await firstValueFrom(
        this.requestService.uploadClaimDocument(claimUuid, toUpload, docSlug)
      );
      const req = this.selectedRequest();
      if (req?.claim) {
        this._setClaimDocuments(req, res, docSlug);
      }
      return true;
    } catch {
      await Swal.fire({
        icon: 'error',
        title: 'No se pudo adjuntar el documento',
        text: 'El archivo no se subió. Intente nuevamente antes de aprobar.'
      });
      return false;
    }
  }

  nextStep(): void {
    if (this.currentStep() < this.timelineSteps().length) {
      this.currentStep.update(step => step + 1);
    }
  }

  prevStep(): void {
    if (this.currentStep() > 1) {
      this.currentStep.update(step => step - 1);
    }
  }
}